import { useEffect, useRef } from "react";

export default function HeroSection() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = [titleRef.current, subtitleRef.current, ctaRef.current, statsRef.current];
    els.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(28px)";
      setTimeout(() => {
        if (!el) return;
        el.style.transition = "opacity 0.8s ease, transform 0.8s ease";
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      }, 300 + i * 180);
    });
  }, []);

  return (
    <section className="relative w-full min-h-screen overflow-hidden flex flex-col justify-center">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/34059bf011f8b6d26afd9c694635afc4.jpg"
          alt="hero background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-bg/85 via-brand-bg/65 to-brand-bg" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-bg/80 via-brand-bg/40 to-transparent" />
        <div
          className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-25 pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(168,85,247,0.5) 0%, transparent 70%)" }}
        />
        <div
          className="absolute bottom-0 right-0 w-80 h-80 rounded-full opacity-15 pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(20,241,217,0.5) 0%, transparent 70%)" }}
        />
      </div>

      {/* Floating Badges — desktop only, positioned to not overlap content */}
      <div className="absolute top-28 right-8 xl:right-28 hidden xl:block animate-float z-10">
        <div className="glass rounded-2xl px-4 py-3 border border-brand-purple/30">
          <p className="text-xs text-brand-purple font-display font-medium whitespace-nowrap">IP × BUSINESS</p>
          <p className="text-white text-sm font-medium mt-0.5 whitespace-nowrap">창작 IP 비즈니스화</p>
        </div>
      </div>
      <div className="absolute bottom-40 right-10 xl:right-36 hidden xl:block z-10">
        <div className="glass rounded-2xl px-4 py-3 border border-brand-teal/30 animate-float" style={{ animationDelay: "1.5s" }}>
          <p className="text-xs text-brand-teal font-display font-medium whitespace-nowrap">TECH × ART</p>
          <p className="text-white text-sm font-medium mt-0.5 whitespace-nowrap">D2C 인프라 구축</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-20 md:pt-24 pb-16 md:pb-20">
        <div className="max-w-2xl lg:max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 glass rounded-full px-3 md:px-4 py-1.5 mb-5 border border-brand-purple/30">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-pulse block flex-shrink-0"></span>
            <span className="text-xs text-white/70 font-body tracking-wide uppercase leading-none">
              Premium Company Builder for Creators
            </span>
          </div>

          {/* Main Headline */}
          <h1
            ref={titleRef}
            className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white flex flex-col gap-2 lg:gap-6"
          >
            <span>당신의 예술,</span>
            <span className="gradient-text-brand">이제 비즈니스가</span>
            <span>됩니다.</span>
          </h1>

          {/* Subtitle */}
          <p
            ref={subtitleRef}
            className="mt-5 md:mt-6 text-sm md:text-base lg:text-lg text-white/60 font-body leading-loose max-w-lg"
          >
            아르트레너는 크리에이터를 위한 프리미엄 컴퍼니빌더입니다.
            자본, 기술, 전략을 제공하여 고유한 IP를 지속 가능한 기업으로 전환합니다.
          </p>

          {/* CTA Buttons */}
          <div ref={ctaRef} className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href="/diagnosis"
              className="group flex items-center justify-center gap-2 px-6 md:px-8 py-3.5 md:py-4 rounded-full font-display font-semibold text-sm md:text-base text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{
                background: "linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)",
                boxShadow: "0 0 30px rgba(168,85,247,0.4), 0 0 60px rgba(168,85,247,0.2)",
              }}
            >
              아르트레너 진단으로 시작하기
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </a>
            <a
              href="#solutions"
              className="flex items-center justify-center gap-2 px-6 md:px-8 py-3.5 md:py-4 rounded-full font-display font-semibold text-sm md:text-base text-white/80 hover:text-white glass border border-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-brand-teal"></i>
              서비스 살펴보기
            </a>
          </div>

          {/* Stats */}
          <div ref={statsRef} className="mt-10 md:mt-14 flex flex-wrap gap-6 md:gap-10">
            {[
              { value: "200+", label: "아티스트 파트너", color: "text-brand-purple" },
              { value: "98%", label: "브랜딩 만족도", color: "text-brand-teal" },
              { value: "3.2x", label: "평균 수익 성장", color: "text-brand-orange" },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col">
                <span className={`font-display text-xl md:text-2xl font-bold ${stat.color}`}>{stat.value}</span>
                <span className="text-xs text-white/40 mt-0.5 font-body">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-brand-bg to-transparent z-10 pointer-events-none" />

      {/* Scroll indicator — desktop only */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center gap-2 opacity-40">
        <span className="text-xs text-white/50 font-body tracking-widest uppercase">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-white/50 to-transparent animate-pulse" />
      </div>
    </section>
  );
}
